import { ExitIcon, InfoCircledIcon, PersonIcon } from "@radix-ui/react-icons";
import {
  Button,
  Callout,
  DropdownMenu,
  Flex,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { logout } from "@/entities/session";
import { useStaffMeQuery } from "@/shared/api";
import { useAppDispatch } from "@/app/appStore";
import { appSession } from "@/shared/lib/storage";
import { useAuthRedirect } from "@/shared/hooks/useAuthRedirect";
import { useHeaderNavigationLinks } from "@/shared/hooks/useHeaderNavigationLinks";
import { NavigationItemId } from "@/shared/types/navigation";
import { QuickNavigationButtons } from "./quick-navigation-buttons";

const Header = memo(
  ({
    title,
    hasWarning = false,
    exclude = [],
  }: {
    title?: string;
    hasWarning?: boolean;
    exclude?: NavigationItemId[];
  }) => {
    const dispatch = useAppDispatch();
    const { redirectToLoginAfterLogout } = useAuthRedirect();
    const { data: me, status } = useStaffMeQuery();
    const [isLoading, setLoading] = useState(false);

    const navigationItems = useHeaderNavigationLinks({ exclude });

    const handleLogout = () => {
      dispatch(logout())
        .unwrap()
        .then(() => {
          setLoading(false);
          appSession.removeItem();
          redirectToLoginAfterLogout();
        })
        .catch(() => {
          setLoading(false);
        });
    };

    return (
      <>
        {hasWarning && (
          <Callout.Root
            color="yellow"
            size="1"
            className="fixed top-0 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-lg z-50 mt-2"
          >
            <Callout.Icon>
              <InfoCircledIcon />
            </Callout.Icon>
            <Callout.Text>Refresh the page to stay up-to-date.</Callout.Text>
          </Callout.Root>
        )}
        <header className="h-10 px-4 py-1 border-y border-gray-200 flex justify-between items-center">
          <Flex align="center" gap="4">
            <Text
              as="div"
              className="flex items-center gap-2"
              size="2"
              weight="bold"
            >
              <figure>
                <img
                  width={16}
                  height={16}
                  src="/assets/images/logo/logo.svg"
                />
              </figure>
              {title}
            </Text>
            <QuickNavigationButtons exclude={exclude} />
          </Flex>
          <div className="flex items-center">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <Button variant="ghost" className="m-0" color="gray">
                  <PersonIcon />
                  <Skeleton
                    loading={status === QueryStatus.pending}
                    className="min-w-20 h-4"
                  >
                    {me?.result?.first_name} {me?.result?.last_name}
                  </Skeleton>
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content className="min-w-[170px]">
                {navigationItems.map((item) => (
                  <DropdownMenu.Item className="p-0" key={item.id}>
                    <Link
                      to={item.to}
                      className="flex items-center gap-2 w-full p-3 cursor-default"
                    >
                      <item.icon />
                      {item.label}
                    </Link>
                  </DropdownMenu.Item>
                ))}
                <DropdownMenu.Item
                  className="p-0"
                  disabled={isLoading}
                  onClick={handleLogout}
                >
                  <Flex gap="2" className="p-2" align="center">
                    <ExitIcon /> Logout
                  </Flex>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </div>
        </header>
      </>
    );
  }
);

export default Header;
